import { useState, useEffect } from 'react';
import ICAL from 'ical.js';
import { apiFetch } from '../api';

function AirbnbCalendar({ bookingData, refreshKey }) {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewDate, setViewDate] = useState(new Date());

  useEffect(() => {
    async function fetchReservations() {
      try {
        const res = await apiFetch('/api/calendar/airbnb');
        const text = await res.text();
        const parsed = ICAL.parse(text);
        const comp = new ICAL.Component(parsed);
        const vevents = comp.getAllSubcomponents('vevent');

        const all = vevents
          .map((ve) => {
            const event = new ICAL.Event(ve);
            const summary = event.summary || '';
            return {
              id: event.uid || '',
              summary,
              checkIn: event.startDate.toJSDate(),
              checkOut: event.endDate.toJSDate(),
            };
          })
          .filter((r) => r.summary !== 'Airbnb (Not available)')
          .sort((a, b) => a.checkIn - b.checkIn);

        setReservations(all);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    fetchReservations();
  }, [refreshKey]);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  function prevMonth() { setViewDate(new Date(year, month - 1, 1)); }
  function nextMonth() { setViewDate(new Date(year, month + 1, 1)); }
  function goToday() { setViewDate(new Date()); }

  const firstDay = new Date(year, month, 1);
  const startOffset = firstDay.getDay();
  const cells = [];
  const gridStart = new Date(year, month, 1 - startOffset);
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push(d);
  }

  function dateKey(d) {
    return d.toLocaleDateString('en-US', { timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit' });
  }

  // iCal DTEND is the checkout day (exclusive of nights stayed). Mark nights
  // booked, and tag the checkout day itself as the "leaving" indicator so the
  // calendar shows guests checking out the day after their last night.
  const dateMap = {};
  function setEntry(key, entry) {
    const existing = dateMap[key];
    if (!existing) { dateMap[key] = entry; return; }
    // Back-to-back: one guest checks out as another checks in on the same day.
    if (existing.position === 'checkout' && entry.position === 'checkin') {
      dateMap[key] = { ...entry, position: 'checkout-checkin', previousReservation: existing.reservation };
    } else if (existing.position === 'checkin' && entry.position === 'checkout') {
      dateMap[key] = { ...existing, position: 'checkout-checkin', previousReservation: entry.reservation };
    } else {
      dateMap[key] = entry;
    }
  }
  reservations.forEach((res) => {
    const d = new Date(res.checkIn);
    const end = new Date(res.checkOut); // checkout day per iCal (day after last night)
    while (d <= end) {
      const key = dateKey(d);
      const isCheckIn = d.toDateString() === res.checkIn.toDateString();
      const isCheckOut = d.toDateString() === end.toDateString();
      const position = isCheckIn ? 'checkin' : isCheckOut ? 'checkout' : 'mid';
      setEntry(key, { reservation: res, position });
      d.setDate(d.getDate() + 1);
    }
  });

  function getGuestName(res) {
    const custom = bookingData[res.id]?.name;
    return custom || res.summary || 'Guest';
  }

  const today = new Date();
  const todayKey = dateKey(today);
  const monthLabel = viewDate.toLocaleDateString('en-US', { timeZone: 'America/Chicago', month: 'long', year: 'numeric' });
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <section className="card airbnb-cal-section">
      <h2>Airbnb Calendar</h2>
      <div className="cal-header">
        <button className="cal-nav-btn" onClick={prevMonth}>&lt;</button>
        <span className="cal-month-label" onClick={goToday}>{monthLabel}</span>
        <button className="cal-nav-btn" onClick={nextMonth}>&gt;</button>
      </div>
      {loading ? (
        <p className="loading">Loading...</p>
      ) : (
        <div className="ab-cal-grid">
          {dayNames.map((d) => (
            <div key={d} className="cal-day-name">{d}</div>
          ))}
          {cells.map((date, i) => {
            const key = dateKey(date);
            const isCurrentMonth = date.getMonth() === month;
            const isToday = key === todayKey;
            const entry = dateMap[key];

            let cellClass = 'ab-cal-cell';
            if (!isCurrentMonth) cellClass += ' other-month';
            if (isToday) cellClass += ' today';
            if (entry) cellClass += ` booked booked-${entry.position}`;

            return (
              <div key={i} className={cellClass}>
                <span className="ab-cal-date">{date.getDate()}</span>
                {entry && (entry.position === 'checkin' || entry.position === 'checkout-checkin') && (
                  <span className="ab-cal-guest">{getGuestName(entry.reservation)}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default AirbnbCalendar;
